import React from 'react';
import { Link, SkeletonLine } from '@one-thd/sui-atomic-components';
import { RETURN_METHOD, ReturnMethodType } from '@src/constants/general';
import {
  CheckEligibilityMap,
  ReturnAllowedMap,
} from '@src/utils/eligibility-utils';
import { getFeatureSwitchStatic } from '@src/utils/general';
import { isROSEnabled } from '@src/constants/fs-values';
import { IReturnEligibilityResponse } from '../../models/api';
import {
  OrderDetailsLineItemSessionObj,
  OrderDetailsSessionDataV2,
} from '../../models/returns';

export const InStoreReturnMethodDescription = (
  hostname: string,
  isMilitaryExchangeGuest: boolean,
) => (
  <>
    Take your items to any{' '}
    <Link href={`https://${hostname}/l/`} target="_blank" variant="body-sm">
      Home Depot store
    </Link>
    . Show your barcode
    {!isMilitaryExchangeGuest && <>, receipt or the card used at purchase</>} to
    an associate at the returns counter.
  </>
);

export const UpsDropOffReturnMethodDescription = (
  <>
    Pack your items in a box and drop them off at{' '}
    <Link
      href="https://www.ups.com/dropoff?loc=en_US"
      target="_blank"
      variant="body-sm"
    >
      select UPS location
    </Link>{' '}
    using your barcode.
  </>
);

export const LORPReturnMethodDescription = (
  <>Schedule a pickup, and The Home Depot will collect your large item(s).</>
);

export const getReturnMethodEligibility = (
  orderDetailsV2: OrderDetailsSessionDataV2,
  returnEligibilityData: IReturnEligibilityResponse | null,
) => {
  // Order Level Flag checks
  let returnInStoreAllowed = false;
  let returnDropoffAllowed = false;
  let returnLORPAllowed = false;

  // from OMS, If return eligibility is not allowed for in-store, hide the in-store option
  if (orderDetailsV2.returnInStoreAllowed) {
    returnInStoreAllowed = true;
  }
  // from OMS, If return eligibility is not allowed for UPS, hide the UPS option
  if (
    orderDetailsV2.returnOnlineAllowed ||
    orderDetailsV2.returnDropoffAllowed
  ) {
    returnDropoffAllowed = true;
  }
  // from OMS, If return eligibility is not allowed for LORP, hide the LORP option
  if (orderDetailsV2.returnLORPAllowed) {
    returnLORPAllowed = true;
  }

  // check if any of the OMS-ELIGIBLE line items for LORP are also CheckEligibilityAPI-ELIGIBLE
  if (
    !checkAnyItemEligible(
      orderDetailsV2.lineItems,
      returnEligibilityData,
      RETURN_METHOD.LORP,
    )
  ) {
    returnLORPAllowed = false;
  }
  // same for in-store returns
  if (
    !checkAnyItemEligible(
      orderDetailsV2.lineItems,
      returnEligibilityData,
      RETURN_METHOD.UPS_DROP_OFF,
    )
  ) {
    returnDropoffAllowed = false;
  }
  // same for in-store returns, but only until SORIS is 4reals...

  return { returnInStoreAllowed, returnDropoffAllowed, returnLORPAllowed };
};

export const checkIsInStoreOnlyReturn = (
  orderDetailsV2: OrderDetailsSessionDataV2,
  returnEligibilityDetails: IReturnEligibilityResponse,
) => {
  const inStoreEligible = orderDetailsV2.returnInStoreAllowed;

  // ROS: LORP left the eligibility contract (it moved to the
  // Return Methods endpoint), so UPS and LORP/pickup are read from the O&P flags
  // ONLY — no eligibility AND. SORIS is the one method still carried by
  // eligibility, so the decision still waits for checkElig to read
  // sorisOrderReturnEligible. In-store-only orders that O&P flags cannot detect
  // are caught later from the Return Methods response (owned by the methods
  // stories), because the O&P flags are not all-inclusive.
  const rosEnabled = isROSEnabled();

  const upsEligible = rosEnabled
    ? orderDetailsV2.returnOnlineAllowed || orderDetailsV2.returnDropoffAllowed
    : (orderDetailsV2.returnOnlineAllowed ||
        orderDetailsV2.returnDropoffAllowed) &&
      returnEligibilityDetails.orderReturnEligible;

  const lorpEligible = rosEnabled
    ? orderDetailsV2.returnLORPAllowed
    : orderDetailsV2.returnLORPAllowed &&
      returnEligibilityDetails.lorpOrderReturnEligible;

  // When SORIS is enabled and the order is SORIS-eligible, do not short-circuit
  // to Phase 0 In-Store — let the normal flow handle it so the SORIS return
  // method is offered. (SORIS eligibility is retained in the v2 contract.)
  const sorisEligible =
    getFeatureSwitchStatic('allowSORIS') &&
    inStoreEligible &&
    returnEligibilityDetails.sorisOrderReturnEligible;

  if (sorisEligible) return false;

  return Boolean(inStoreEligible && !upsEligible && !lorpEligible);
};

export const ReturnMethodSkeletonLoader = () => (
  <div className="sui-mt-4" data-testid="method-skeleton">
    <SkeletonLine variant="multi" numberOfLines={2} />
  </div>
);

export const checkAnyItemEligible = (
  lineItems: OrderDetailsLineItemSessionObj[],
  returnEligibilityData: IReturnEligibilityResponse | null,
  returnMethod: ReturnMethodType,
) => {
  let showMethodBtn = false;

  const eligibilityField = CheckEligibilityMap[returnMethod];
  const omsEligibleField = ReturnAllowedMap[returnMethod];

  const filteredLineItems = lineItems.filter((item) => {
    if (returnMethod === RETURN_METHOD.UPS_DROP_OFF) {
      return item.returnDropoffAllowed || item.returnOnlineAllowed;
    }
    return item[omsEligibleField];
  });

  if (
    // if at least 1 filtered item is eligible in checkEligibility, show the return method
    filteredLineItems?.some(
      (item) =>
        returnEligibilityData?.lineItems?.find(
          (eligibilityItem) => eligibilityItem.lineNumber === item.lineId,
        )?.[eligibilityField],
    )
  ) {
    showMethodBtn = true;
  }
  return showMethodBtn;
};
